import $ from 'jquery';
import Environment from '../environment';
import Eventable from '../eventable';
import '../lib/spatialNavigation';
import { getLogger } from '../logging/logger';
import Android from '../platform/android';

const { SpatialNavigation } = window;
const logger = getLogger('InputManager');

export const DEFAULT_SECTION_ID = 'page';

class InputManager extends Eventable {
	/**
	 * @returns {Record<string, number>}
	 */
	get Keys() {
		return {
			Back: 8,
			Enter: 13,
			Left: 37,
			Up: 38,
			Right: 39,
			Down: 40,
		};
	}

	/**
	 * @returns {Record<string, string>}
	 *
	 * * Note that our linting and formatting tools will force this to
	 * * numbers, but javascript will actually convert it to strings, hence
	 * * the type differences in the property names between docs and code
	 */
	get KeyDirections() {
		return {
			8: 'Back',
			13: 'Enter',
			37: 'Left',
			38: 'Up',
			39: 'Right',
			40: 'Down',
			461: 'Back', // WebOS Back Button
			10009: 'Back', // Tizen Back Key
		};
	}

	/**
	 * @returns {Record<string, number>}
	 *
	 * * Note that our linting and formatting tools will force this to
	 * * numbers, but javascript will actually convert it to strings, hence
	 * * the type differences in the property names between docs and code
	 */
	get KeyNumbers() {
		return {
			48: 0,
			49: 1,
			50: 2,
			51: 3,
			52: 4,
			53: 5,
			54: 6,
			55: 7,
			56: 8,
			57: 9,
		};
	}

	constructor() {
		logger.log('[constructor]');

		super();

		// Spatial navigator configuration
		SpatialNavigation.init();
		SpatialNavigation.set({
			straightOverlapThreshold: 0.15,
		});
		SpatialNavigation.add(DEFAULT_SECTION_ID, {
			selector: '#page .btn:not(:disabled):not(.disabled)',
			rememberSource: true,
			restrict: 'self-first',
			defaultElement: '#page .btn',
		});
		SpatialNavigation.setDefaultSection(DEFAULT_SECTION_ID);

		// Add key listeners on window
		window.addEventListener('keydown', this.onKeyEvent.bind(this), true);
		window.addEventListener('keyup', this.onKeyEvent.bind(this), true);

		// Handle online/offline events for connectivity changes
		window.addEventListener('online', this.onOnline.bind(this));
		window.addEventListener('offline', this.onOffline.bind(this));

		logger.debug(`{${window.location.pathname}}`, {
			isSetTopBox: Environment.isSetTopBox(),
			isCordova: Environment.isCordova(),
			isTizen: Environment.isTizen(),
			isInHub: Environment.isInHub(),
		});

		if (Environment.isInHub()) {
			logger.debug(`[Listening for back-button-pressed from parent window]{${location.pathname}}`);

			// Add listener for message from parent window to trigger onBackButton
			window.addEventListener('message', (event) => {
				const allowedOrigins = [window.location.origin];

				// Add localhost for development if needed
				if (Environment.isLocal()) {
					allowedOrigins.push('http://localhost:8080');
					// Add any other local development URLs you use
				}

				const isValidOrigin = allowedOrigins.includes(event.origin);
				logger.debug('[message event]', { type: event.data?.type, isValidOrigin, allowedOrigins });

				if (isValidOrigin && event.data.type === 'back-button-pressed') {
					this.onBackButton(event);
				}
			});
		} else if (Environment.isCordova()) {
			Android.isReady.then(() => {
				logger.debug(`[Listening for Cordova "backbutton" event]{${location.pathname}}`);
				document.addEventListener('backbutton', this.onBackButton.bind(this), true);
			});
		}

		this.pointerDetected = false;
		$(document.body).one('pointerdown mousemove', this.onPointerDetected.bind(this));
	}

	/**
	 * Dispatch events when a user presses directional buttons
	 */
	onKeyEvent(event) {
		logger.debug('[onKeyEvent]', { event });

		const button = this.KeyDirections[event.keyCode];
		const number = this.KeyNumbers[event.keyCode];
		const type = event.type.replace('key', ''); // 'up' || 'down'

		if (button) {
			if (button === 'Back' && event?.target?.tagName !== 'INPUT') {
				/**
				 * Prevent default browser behaviour for the backspace button.
				 * We will force the user back to the home page or do special handling
				 * for the Back button.
				 */
				event.preventDefault();
			}

			const eventName = `${button.toLowerCase()}-${type.toLowerCase()}`;
			logger.debug('[Handle Button]', { eventName, button, type });
			this.trigger(eventName, event);
			if (type === 'down') {
				// Trigger event with just the button name for convience
				this.trigger(button.toLowerCase(), event);
			}
		} else if (Number.isFinite(number)) {
			const eventName = `${number.toString()}-${type.toLowerCase()}`;
			logger.debug('[Handle Number]', { eventName, number, type });

			this.trigger(eventName, event);
			if (type === 'down') {
				// Trigger event with just the button number for convience
				this.trigger(number.toString(), event);
			}
		}
	}

	/**
	 * Handles the browser 'online' event when connectivity is restored.
	 * Refocuses the current element to restore input responsiveness.
	 */
	onOnline() {
		logger.log(`[s${this.onOnline.name}] Connection restored`);

		// Refocus the currently focused element or default section
		const currentFocus = document.activeElement;
		if (currentFocus && currentFocus !== document.body) {
			logger.debug(`[${this.onOnline.name}] Refocusing current element`, { currentFocus });
			SpatialNavigation.focus(currentFocus);
		} else {
			logger.debug(`[${this.onOnline.name}] Refocusing default section`);
			SpatialNavigation.focus();
		}
	}

	/**
	 * Handles the browser 'offline' event when connectivity is lost.
	 */
	onOffline() {
		logger.log(`[${this.onOffline.name}] Connection lost`);
	}

	/**
	 * Hijacks Android OS 'backbutton' functionality and redispatches as a backspace.
	 */
	onBackButton() {
		logger.log(`[${this.onBackButton.name}]`);

		const isHub = Environment.isHub();
		const loadedIFrame = [...document.querySelectorAll('iframe')].find(
			(iframe) => iframe.src && !iframe.src.startsWith('about:blank'),
		);
		logger.debug(`{${window.location.pathname}}`, { isHub, loadedIFrame });

		if (isHub && loadedIFrame) {
			const { origin: targetOrigin } = new URL(loadedIFrame.src);
			// Dispatch message to iframe that the back button was pressed
			logger.debug(`[Dispatching 'back-button-pressed' to loaded iframe]{${window.location.pathname}}`, { targetOrigin });
			window.frames[0].postMessage({ type: 'back-button-pressed' }, targetOrigin);
		} else {
			document.body.dispatchEvent(new KeyboardEvent('keydown', { keyCode: this.Keys.Back }));
		}
	}

	onPointerDetected() {
		logger.log('[onPointerDetected]');
		this.pointerDetected = true;
	}

	clear(...args) {
		return SpatialNavigation.clear(...args);
	}

	set(...args) {
		return SpatialNavigation.set(...args);
	}

	add(...args) {
		try {
			return SpatialNavigation.add(...args);
		} catch (e) {
			return false;
		}
	}

	remove(...args) {
		return SpatialNavigation.remove(...args);
	}

	disable(...args) {
		return SpatialNavigation.disable(...args);
	}

	enable(...args) {
		return SpatialNavigation.enable(...args);
	}

	pause(...args) {
		return SpatialNavigation.pause(...args);
	}

	resume(...args) {
		return SpatialNavigation.resume(...args);
	}

	focus(...args) {
		// ! Warning - Hardcoded selector for dialog detection
		const dialog = document.querySelector('#dialog');
		if (dialog) {
			const target = args[0];
			const isDialogSectionId = target === 'dialog';
			const targetIsNode = target instanceof Node;
			const targetIsDialog = target === dialog || (targetIsNode && dialog.contains(target));

			if (isDialogSectionId || targetIsDialog) {
				return SpatialNavigation.focus(...args);
			}
			return undefined;
		}

		if (Environment.isSetTopBox()) {
			return SpatialNavigation.focus(...args);
		}

		logger.info('[Only focus on STB devices]');
		return undefined;
	}

	makeFocusable(...args) {
		return SpatialNavigation.makeFocusable(...args);
	}

	move(...args) {
		return SpatialNavigation.move(...args);
	}

	setDefaultSection(...args) {
		return SpatialNavigation.setDefaultSection(...args);
	}
}

export default new InputManager();
